﻿import { LoadingButton } from '@mui/lab';
import { Box, Divider, Grid, TextField, Typography } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import QRCode from 'react-qr-code';
import { useSelector } from 'react-redux';
import { FORM_STATUS } from '../../../model/formStatus';
import { getPatients } from '../../../redux/actions/healthcareProfessionalActions';
import { useAppThunkDispatch } from '../../../redux/configureStore';
import { selectAccountId } from '../../../redux/selectors/accountSelectors';
import { healthcareProfessionalService } from '../../../services/healthcareProfessionalService';

import BackButton from '../../../components/BackButton';
import { ExportButton, PrintableSection, useTemplate } from '../../../components/Print';
import { useClientTheme } from '../../../helpers/hooks/useClientTheme';
import { isValidGUID } from '../../../helpers/utilityFunctions/utilityFunctions';

const defaultState = {
  accountId: '',
};

export const AddPatientPage: React.FC = () => {
  const { theme } = useClientTheme();
  const { t } = useTranslation('addPatient');
  const [formData, setFormData] = React.useState(defaultState);
  const [status, setStatus] = React.useState(FORM_STATUS.IDLE);
  const [touched, setTouched] = React.useState(defaultState);
  const [statusMessage, setStatusMessage] = React.useState('');
  const [requestComplete, setRequestComplete] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const accountId = useSelector(selectAccountId);
  const dispatch = useAppThunkDispatch();

  function handleChange(e: any) {
    e.persist();
    setStatusMessage('');
    setFormData((curFormData) => {
      return {
        ...curFormData,
        [e.target.id]: e.target.value,
      };
    });
  }

  function handleBlur(e: any) {
    e.persist();
    setTouched((cur: any) => {
      return { ...cur, [e.target.id]: true };
    });
  }

  function getErrors(formData: any) {
    const formValue = formData.accountId.trim();
    const result = {
      accountId: '',
    };
    if (status === FORM_STATUS.COMPLETED) return result;
    if (!formValue) {
      result.accountId = t('validation.required');
    } else if (!isValidGUID(formValue)) {
      result.accountId = t('validation.invalid');
    }

    return result;
  }

  //Derived state
  const errors = getErrors(formData);
  const isValid = Object.values(errors).every((x) => x === null || x === '');

  const onSubmit = () => {
    if (requestComplete) {
      if (accountId) {
        dispatch(getPatients(accountId, true));
      }
    }

    if (isValid) {
      setStatus(FORM_STATUS.SUBMITTING);
      setIsLoading(true);
      const formValue = formData.accountId.trim();
      //TODO: "isAuthorizedByPatient" is hardcoded to true here - need to find out what determines this
      healthcareProfessionalService
        .registerPatient(accountId, true, formValue)
        .then((response: any) => {
          setStatus(FORM_STATUS.COMPLETED);
          setFormData(defaultState);
          setRequestComplete(true);
          setStatusMessage(response.message);
        })
        .catch((error: any) => {
          setStatusMessage(error?.message ?? t('result.genericError'));
          setStatus(FORM_STATUS.SUBMITTED);
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      setStatus(FORM_STATUS.SUBMITTED);
    }
  };

  const addPatientDialogContent = (isValid: boolean, isLoading: boolean) => {
    return (
      <>
        <Typography>{t('enterAccountId')}</Typography>
        <Grid container direction="row">
          <Grid size="grow">
            <TextField
              margin="dense"
              type="text"
              fullWidth
              variant="standard"
              required
              id="accountId"
              label={t('accountIdLabel')}
              name="account id"
              value={formData.accountId}
              onChange={handleChange}
              onBlur={handleBlur}
              disabled={status === FORM_STATUS.COMPLETED ? true : false}
              helperText={
                statusMessage ||
                ((touched.accountId || status === FORM_STATUS.SUBMITTED) &&
                  errors.accountId)
              }
              error={
                statusMessage !== '' ||
                ((touched.accountId !== '' ||
                  status === FORM_STATUS.SUBMITTED) &&
                  errors.accountId !== '')
              }
            />
          </Grid>
          <Grid size={2}


            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <LoadingButton
              onClick={() => onSubmit()}
              disabled={!isValid}
              loading={isLoading}
              variant="contained"
              color="primary"
            >
              {t('add')}
            </LoadingButton>
          </Grid>
        </Grid>
      </>
    );
  };

  const addPatientDialogResultMessage = () => {
    return (
      <Grid
        container
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography>{statusMessage}</Typography>
      </Grid>
    );
  };

  return (
    <Box>
      <BackButton />

      <Box sx={{ ml: '10px' }}>
        <Typography variant="h4" component="h4">
          {t('title')}
        </Typography>

        <Box
          sx={{
            mt: '10px',
            width: 'fit-content',
            padding: '20px',
            background: theme.palette.common.white,
            position: 'relative',
          }}
        >
          <Box sx={{ position: 'absolute', top: '-45px', right: 0 }}>
            <ExportButton
              targetId="qr-code-section"
              variant="contained"
              label={t('print.label')}
              tooltip={t('print.tooltip')}
              filename="patient-qr-code.pdf"
            />
          </Box>
          <Typography>{t('intro')}</Typography>
          <Typography sx={{ mt: '8px' }}>
            {t('scanInstruction', { app: theme.productNames.app })}
          </Typography>
          <PrintableSection
            id="qr-code-section"
            title={t('qrCodeTitle')}
            printHeader={null}
            printFooter={null}
          >
            <Grid
              container
              spacing={1}
              sx={{ alignItems: 'center', justifyContent: 'center',  flexDirection: 'column', 
                mt: '8px',
              }}
            >
              <Grid size={{ xs: 12, sm: 12, md: 6, xl: 6 }}>
                <QRCode value={accountId} size={192} />
              </Grid>
              <Grid size={{ xs: 12, sm: 12, md: 6, xl: 6 }}>
                <Typography variant="subtitle1" component="div">
                  {accountId}
                </Typography>
              </Grid>
            </Grid>
          </PrintableSection>
          <Divider
            sx={{
              mt: '8px',
              mb: '8px',
              '&::before, &::after': {
                borderColor: (theme) => theme.palette.primary.main,
              },
            }}
          >
            {t('or')}
          </Divider>
          <Typography>
            {t('shareIdInstruction', { app: theme.productNames.app })}
          </Typography>

          <Box
            sx={{
              mt: '8px',
            }}
          >
            {requestComplete
              ? addPatientDialogResultMessage()
              : addPatientDialogContent(isValid, isLoading)}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default AddPatientPage;
